/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useEffect, useRef, useState } from 'react';

/** Types `text` like a terminal: quick keystrokes with a breath after spaces.
 * Reduced motion (or a completed pass) renders the full text at once.
 * `skip` finishes instantly — wired to clicks AND to any focus entering the
 * terminal, so keyboard users never wait on the animation. */
export const useTypedText = (text: string, animate: boolean) => {
  const [shown, setShown] = useState(animate ? '' : text);
  const timer = useRef<number>();

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!animate) {
      setShown(text);
      return undefined;
    }
    setShown('');
    let i = 0;
    const tick = () => {
      i += 1;
      setShown(text.slice(0, i));
      if (i < text.length) {
        // ~9ms per keystroke, a 32ms breath after word boundaries: the whole
        // install command lands in ~1.2s — alive, never in the way.
        timer.current = window.setTimeout(
          tick,
          text[i - 1] === ' ' ? 32 : 9,
        );
      }
    };
    timer.current = window.setTimeout(tick, 120);
    return () => window.clearTimeout(timer.current);
  }, [text, animate]);

  const skip = () => {
    window.clearTimeout(timer.current);
    setShown(text);
  };

  return { shown, done: shown === text, skip };
};
